import { useState } from 'react';
import { message } from 'antd';
import { deleteAgentConversation, updateAgentConversationTitle } from '@/services/assets/api';
import type { SessionHistoryItem } from '../components/HistoryMenu/types';
import { useHistoryConversationStore, type HistoryConversationActionSource, type HistoryConversationChange } from '@/stores/history-conversation';
import { resolveAgentNameHeader } from '@/utils/historySession';

interface UseHistoryItemActionsOptions {
  source?: HistoryConversationActionSource;
  onActionSuccess?: (change: HistoryConversationChange) => Promise<void> | void;
}

const useHistoryItemActions = ({ source, onActionSuccess }: UseHistoryItemActionsOptions = {}) => {
  const notifyHistoryChanged = useHistoryConversationStore((state) => state.notifyHistoryChanged);
  const [mutating, setMutating] = useState(false);
  const [renameVisible, setRenameVisible] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [activeItem, setActiveItem] = useState<SessionHistoryItem | null>(null);

  const closeRenameModal = () => {
    setRenameVisible(false);
    setActiveItem(null);
  };

  const closeDeleteModal = () => {
    setDeleteVisible(false);
    setActiveItem(null);
  };

  const openRenameModal = (item: SessionHistoryItem) => {
    setActiveItem(item);
    setRenameVisible(true);
  };

  const openDeleteModal = (item: SessionHistoryItem) => {
    setActiveItem(item);
    setDeleteVisible(true);
  };

  const handleRenameConfirm = async (title: string) => {
    if (!activeItem || !source) return;

    const sessionId = activeItem.session_id || activeItem.id;
    const headers = { 'X-Agent-Name': resolveAgentNameHeader(activeItem) };
    setMutating(true);
    try {
      const res = await updateAgentConversationTitle(sessionId, { title }, { headers });
      if (res?.code !== 200) {
        throw new Error(res?.msg || '重命名失败');
      }
      message.success('重命名成功');
      const change: HistoryConversationChange = { type: 'rename', sessionId, title, source };
      closeRenameModal();
      await onActionSuccess?.(change);
      notifyHistoryChanged(change);
    } catch (error: any) {
      message.error(error?.message || '重命名失败');
    } finally {
      setMutating(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!activeItem || !source) return;

    const sessionId = activeItem.session_id || activeItem.id;
    const headers = { 'X-Agent-Name': resolveAgentNameHeader(activeItem) };
    setMutating(true);
    try {
      const res = await deleteAgentConversation(sessionId, { headers });
      if (res?.code !== 200) {
        throw new Error(res?.msg || '删除失败');
      }
      message.success('删除成功');
      const change: HistoryConversationChange = { type: 'delete', sessionId, source };
      closeDeleteModal();
      await onActionSuccess?.(change);
      notifyHistoryChanged(change);
    } catch (error: any) {
      message.error(error?.message || '删除失败');
    } finally {
      setMutating(false);
    }
  };

  return {
    activeItem,
    closeDeleteModal,
    closeRenameModal,
    deleteVisible,
    handleDeleteConfirm,
    handleRenameConfirm,
    mutating,
    openDeleteModal,
    openRenameModal,
    renameVisible,
  };
};

export default useHistoryItemActions;
